/**
 * InlineDdlRoutineBody — the embeddable content (parked-statement scan +
 * `RoutineEditFormBody`), consumed directly by the Compact DDL editor's
 * `compactDdlBodyArm`.
 *
 * Mode selection (`create` vs `alter`) is driven by the parked statement's
 * leading keyword:
 *   - CREATE PROCEDURE … / CREATE FUNCTION … → create mode.
 *   - ALTER PROCEDURE … / ALTER FUNCTION …   → alter mode (oracle COMPILE /
 *     pgsql RENAME TO).
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D7 — Procedure / Function shell UI.
 */
import { useMemo } from 'react';

import type { VqeDdlEditContext } from '../edit/editContext';
import {
  RoutineEditFormBody,
  type RoutineFormValues,
} from '../render/common/RoutineEditFormBody';
import { valuesFromParsed } from '../render/common/routineFormHelpers';
import {
  extractParkedRoutine,
  type ParsedParkedRoutine,
} from '../../../sql-resolver/ddl/extract/routine';

export interface InlineDdlRoutineBodyProps {
  readonly editContext: VqeDdlEditContext;
  /** Test seam — overrides the parsed parked-routine (skips the scan). */
  readonly parsedOverride?: ParsedParkedRoutine | null;
  /** Invoked after a successful commit or cancel, so an embedding popover can close. */
  readonly onDone?: () => void;
  /**
   * Cancel-only escape, when the host wants Cancel and commit to mean
   * DIFFERENT things. Falls back to `onDone` when omitted, so every existing
   * caller keeps the old "commit or cancel both call `onDone`" behaviour.
   * The compact popover passes it to close on Cancel WITHOUT closing on a
   * successful commit (the commit funnels through the app-shell
   * PreviewDialog, which needs this body’s context to stay mounted).
   */
  readonly onCancel?: () => void;
}

export function InlineDdlRoutineBody({
  editContext,
  parsedOverride,
  onDone,
  onCancel,
}: InlineDdlRoutineBodyProps) {
  const parsed = useMemo<ParsedParkedRoutine | null>(() => {
    if (parsedOverride !== undefined) return parsedOverride;
    try {
      const buf = editContext.service.getParsedBuffer(editContext.documentId);
      if (!buf) return null;
      const stmt = buf.documentStructure.statements.find(
        (s) => s.id === editContext.statementId,
      );
      if (!stmt) return null;
      return extractParkedRoutine(buf, stmt);
    } catch {
      return null;
    }
  }, [parsedOverride, editContext.service, editContext.documentId, editContext.statementId]);

  const mode: 'create' | 'alter' = parsed?.action === 'alter' ? 'alter' : 'create';
  const initialValues: Partial<RoutineFormValues> | undefined = parsed
    ? valuesFromParsed(parsed)
    : undefined;

  return (
    <RoutineEditFormBody
      editContext={editContext}
      mode={mode}
      initialValues={initialValues}
      testId="inline-ddl-routine-form"
      onCommitted={() => onDone?.()}
      onCancel={() => (onCancel ?? onDone)?.()}
    />
  );
}
